import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiIcon, MiIconButton, MiInputTextbox } from "@miplatform/components";
import { getCurrencyString } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiCheck,
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiWindowClose,
} from "@mdi/js";
import { Box, CircularProgress, Tooltip, useTheme } from "@mui/material";

const itemSchema = z.object({
  name: z.string().min(1, "Required"),
  quantity: z.coerce
    .number()
    .int("Quantity must be a whole number")
    .min(0, "Quantity cannot be negative"),
  unitPrice: z.coerce.number(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

export type QCCameraLineItemType = {
  id: string;
  tempId?: string;
  name: string;
  quantity: number;
  unitPrice: number;
  modifiedOn?: string;
  isNew?: boolean;
  isSubmitting?: boolean;
};

type QCCameraItemProps = {
  item: QCCameraLineItemType;
  onUpdate: (item: QCCameraLineItemType) => void | Promise<void>;
  onDelete: (item: QCCameraLineItemType) => void;
};

export const QCCameraItem = ({
  item,
  onUpdate,
  onDelete,
}: QCCameraItemProps) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(Boolean(item.isNew));
  const [isSaving, setIsSaving] = useState(false);
  const isSubmitting = Boolean(item.isSubmitting);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<ItemFormValues>({
    mode: "onSubmit",
    defaultValues: {
      name: item.name || "",
      quantity: item.quantity ?? 1,
      unitPrice: item.unitPrice ?? 0,
    },
    resolver: zodResolver(itemSchema),
  });

  const quantity = Number(watch("quantity") || 0);
  const unitPrice = Number(watch("unitPrice") || 0);
  const totalValue = unitPrice * (quantity || 0);
  const isFormReadOnly = !isEditing || isSubmitting || isSaving;

  const handleStartEditing = () => {
    clearErrors();
    setIsEditing(true);
  };

  const handleSave = async (data: ItemFormValues) => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await onUpdate({
        ...item,
        name: data.name,
        quantity: Number(data.quantity),
        unitPrice: Number(data.unitPrice),
      });
      setIsEditing(false);
    } catch {
      reset({
        name: item.name || "",
        quantity: item.quantity ?? 1,
        unitPrice: item.unitPrice ?? 0,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (item.tempId && item.isNew) {
      onDelete(item);
      return;
    }

    reset({
      name: item.name || "",
      quantity: item.quantity ?? 1,
      unitPrice: item.unitPrice ?? 0,
    });
    setIsEditing(false);
  };

  return (
    <Box
      className={cx(classes.root, { [classes.submitting]: isSubmitting })}
      data-testid="qc-camera-item"
    >
      <Box className={classes.fields}>
        <Box className={classes.nameField}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Tooltip title={field.value || ""} arrow placement="top">
                <Box className={cx(classes.fieldBox)}>
                  <MiInputTextbox
                    {...field}
                    className={cx(classes.input, classes.nameInput)}
                    size="small"
                    label="Item"
                    error={errors.name}
                    required
                    helperText={errors.name?.message}
                    disabled={isFormReadOnly}
                  />
                </Box>
              </Tooltip>
            )}
          />
        </Box>
        <Box className={classes.quantityField}>
          <Controller
            name="quantity"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Tooltip title={field.value ?? ""} arrow placement="top">
                <Box className={cx(classes.fieldBox)}>
                  <MiInputTextbox
                    {...field}
                    className={cx(classes.input, classes.numericInput)}
                    size="small"
                    label="Qty"
                    type="number"
                    error={errors.quantity}
                    required
                    helperText={errors.quantity?.message}
                    disabled={isFormReadOnly}
                  />
                </Box>
              </Tooltip>
            )}
          />
        </Box>
        <Box className={classes.priceField}>
          <Controller
            name="unitPrice"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Tooltip
                title={
                  field.value != null
                    ? getCurrencyString(Number(field.value))
                    : ""
                }
                arrow
                placement="top"
              >
                <Box className={cx(classes.fieldBox)}>
                  <NumericFormat
                    value={field.value ?? ""}
                    customInput={MiInputTextbox}
                    className={cx(classes.input, classes.numericInput)}
                    thousandSeparator
                    required
                    label="Price"
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative
                    allowLeadingZeros={false}
                    prefix="$"
                    name={field.name}
                    onValueChange={(values) => {
                      field.onChange(values.value);
                    }}
                    error={errors.unitPrice}
                    helperText={errors.unitPrice?.message}
                    disabled={isFormReadOnly}
                  />
                </Box>
              </Tooltip>
            )}
          />
        </Box>
        <Box className={classes.totalField}>
          <Tooltip title={getCurrencyString(totalValue)} arrow placement="top">
            <Box className={cx(classes.totalBox)}>
              <MiInputTextbox
                className={cx(classes.input, classes.numericInput)}
                disabled={isFormReadOnly}
                label="Total"
                value={getCurrencyString(totalValue)}
                placeholder="$0.00"
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>
      <Box className={classes.actions}>
        {isSubmitting ? (
          <Box className={classes.submittingState}>
            <CircularProgress size={20} color="primary" />
          </Box>
        ) : isEditing ? (
          <>
            <MiIconButton
              onClick={handleSubmit(handleSave)}
              data-testid="save-qc-camera-item-btn"
              size="small"
              disabled={isSaving}
            >
              <MiIcon
                color={theme.palette.primary.main}
                path={mdiCheck}
                size={0.8}
              />
            </MiIconButton>
            <MiIconButton
              onClick={handleCancel}
              data-testid="close-qc-camera-item-btn"
              size="small"
              disabled={isSaving}
            >
              <MiIcon
                color={theme.palette.red.main}
                path={mdiWindowClose}
                size={0.8}
              />
            </MiIconButton>
          </>
        ) : (
          <>
            <MiIconButton
              onClick={handleStartEditing}
              data-testid="edit-qc-camera-item-btn"
              size="small"
            >
              <MiIcon
                color={theme.palette.grey.main}
                path={mdiPencilOutline}
                size={0.8}
              />
            </MiIconButton>
            <MiIconButton
              onClick={() => onDelete(item)}
              data-testid="delete-qc-camera-item-btn"
              size="small"
            >
              <MiIcon
                color={theme.palette.grey.main}
                path={mdiTrashCanOutline}
                size={0.8}
              />
            </MiIconButton>
          </>
        )}
      </Box>
    </Box>
  );
};
const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  submitting: {
    opacity: 0.8,
  },
  submittingState: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 60,
  },
  fields: {
    display: "flex",
    flex: 1,
    alignItems: "flex-start",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  nameField: {
    flex: "6 1 0",
    minWidth: 0,
  },
  quantityField: {
    flex: "1.5 1 0",
    minWidth: 0,
  },
  priceField: {
    flex: "2.25 1 0",
    minWidth: 0,
  },
  totalField: {
    flex: "2.25 1 0",
    minWidth: 0,
  },
  fieldBox: {
    width: "100%",
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1),
  },
  totalBox: {
    width: "100%",
    backgroundColor: theme.palette.grey.lightAccent,
    borderRadius: theme.spacing(1),
  },
  actions: {
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
  },
  input: {
    "& .MuiInputBase-input": {
      fontSize: theme.fontSize.medium,
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.common.black,
      color: theme.palette.common.black,
    },
  },
  nameInput: {
    "& .MuiInputBase-input": {
      textAlign: "left",
    },
  },
  numericInput: {
    "& .MuiInputBase-input": {
      textAlign: "right",
    },
  },
}));
